/**
 * Persistent banners under the top bar (web.md §1, §4 "Additions"): no durable storage ("This browser
 * cannot keep data after you close the tab."), today's message limit reached (with More → /subscribe),
 * the Jev scoring pause, and from data schema 3 (models.md §6.4, §9.5, §12):
 *   - "N messages are waiting for the on-device model · Download (135 MB)" while a switched-on on-device
 *     model is not installed here and messages wait for it;
 *   - "The browser removed the downloaded model. Download it again." after an eviction;
 *   - "A new version is available — reload" when the server speaks a newer data schema (sync_conflict);
 *   - "Jev is not answering …" while Jev's requests fail and its messages wait for the retry
 *     (`PipelineStatus.models[].waiting = 'unavailable'`; after scoring.max_attempts the rows fail and the
 *     next scoring rules decide).
 * All use the shared tokens.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EmbeddingCatalogEntry, LimitCheck, PipelineStatus } from '@digester/core';
import { cannotStart, megabytes } from '../settings/models/catalog.js';
import { entryOf, isOnDevice, useCatalog, useInstalls, useModels } from '../settings/models/data.js';
import { useModelDownload } from '../settings/models/InstallBlock.js';
import { Icon } from '../icons/Icon.js';
import { useEngineEvent, useEngineQuery, useEngineStatus } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { formatExact } from '../ui/format.js';
import { scoringPauseText } from './scoring-pause.js';

const utcToday = (): string => new Date().toISOString().slice(0, 10);

interface ModelWait {
  entry: EmbeddingCatalogEntry;
  pending: number;
  evicted: boolean;
  /** Installed, but the session could not start on this device (review M-R2): Retry, not Download. */
  failed: boolean;
}

/**
 * Switched-on on-device models that are not installed on this device while messages wait for them
 * (`PipelineStatus.models[].waiting = 'download'`), or whose files the browser evicted. A download in
 * progress shows in the status cluster instead.
 */
function useModelWaits(status: PipelineStatus | undefined): ModelWait[] {
  const { models } = useModels();
  const catalog = useCatalog();
  const installs = useInstalls();
  const out: ModelWait[] = [];
  for (const m of models) {
    if (!m.enabled || !m.entitled) continue;
    const entry = entryOf(catalog, m.id);
    if (!isOnDevice(entry)) continue;
    const install = installs.byId.get(m.id);
    if (!install || !(install.phase === 'absent' || install.phase === 'error')) continue;
    const owed = status?.models.find((s) => s.modelId === m.id);
    const pending = owed?.pending ?? 0;
    if (install.evicted || (pending > 0 && (owed?.waiting === 'download' || owed?.waiting === 'error' || owed?.waiting === null || owed?.waiting === undefined))) {
      out.push({ entry, pending, evicted: install.evicted, failed: install.phase === 'error' && cannotStart(install) });
    }
  }
  return out;
}

export function Banners() {
  const { durable } = useEngineStatus();
  const download = useModelDownload();
  const navigate = useNavigate();
  const [limit, setLimit] = useState<LimitCheck | null>(null);
  const [dayOfLimit, setDayOfLimit] = useState<string | null>(null);
  const [dismissedDay, setDismissedDay] = useState<string | null>(null);
  // The limit event arrives once per day; the pipeline status keeps the banner after a reload.
  const status = useEngineQuery(qk.pipeline(), (e) => e.pipeline.status()).data;
  useEngineEvent((event) => {
    if (event.type === 'limit' && event.check.limit === 'messages_per_day' && !event.check.ok) {
      setLimit(event.check);
      setDayOfLimit(utcToday());
    }
  });
  const fromStatus: LimitCheck | null = status && status.messagesPerDayLimit > 0 && status.messagesToday >= status.messagesPerDayLimit
    ? { limit: 'messages_per_day', max: status.messagesPerDayLimit, used: status.messagesToday, ok: false }
    : null;
  const current = limit !== null && dayOfLimit === utcToday() ? limit : fromStatus;
  const limitToday = current !== null && dismissedDay !== utcToday();
  const paused = status?.scoringPaused ?? null;
  const [dismissedPause, setDismissedPause] = useState<number | null>(null);
  const pauseShown = paused !== null && dismissedPause !== paused.until;
  const waiting = useModelWaits(status);
  const [dismissedWaits, setDismissedWaits] = useState<ReadonlySet<string>>(new Set());
  const waits = waiting.filter((w) => !dismissedWaits.has(`${w.entry.id}:${w.evicted ? 'evicted' : 'waiting'}`));
  const sync = useEngineQuery(qk.sync(), (e) => e.sync.status()).data;
  const reload = sync?.error?.code === 'sync_conflict';
  const { models } = useModels();
  const [dismissedDown, setDismissedDown] = useState<ReadonlySet<string>>(new Set());
  const down = (status?.models ?? []).filter((m) => m.waiting === 'unavailable' && m.pending > 0 && !dismissedDown.has(m.modelId));
  if (durable !== false && !limitToday && !pauseShown && waits.length === 0 && !reload && down.length === 0) return null;
  return (
    <div className="app-banners">
      {reload ? (
        <div className="app-banner" role="status" data-banner="reload">
          <Icon name="refresh-cw" />
          <span>A new version is available — reload. Your changes on this device are kept and sync after the reload.</span>
          <button type="button" className="text-link" data-action="reload" onClick={() => location.reload()}>
            Reload
          </button>
        </div>
      ) : null}
      {waits.map((w) => (
        <div className="app-banner" role="status" data-banner={w.evicted ? 'model-evicted' : w.failed ? 'model-failed' : 'model-waiting'} data-model={w.entry.id} key={w.entry.id}>
          <Icon name={w.evicted || w.failed ? 'circle-alert' : 'download'} />
          <span>
            {w.evicted
              ? `The browser removed the downloaded model. Download it again.${w.pending > 0 ? ` ${formatExact(w.pending)} message${w.pending === 1 ? ' is' : 's are'} waiting for it.` : ''}`
              : w.failed
                ? `The on-device model could not be started on this device: ${formatExact(w.pending)} message${w.pending === 1 ? ' is' : 's are'} waiting for it`
                : `${formatExact(w.pending)} message${w.pending === 1 ? ' is' : 's are'} waiting for the on-device model`}
          </span>
          {w.failed ? (
            <button type="button" className="text-link" data-action="banner-retry" onClick={() => download(w.entry)}>
              Retry
            </button>
          ) : (
            <button type="button" className="text-link" data-action="banner-download" onClick={() => download(w.entry)}>
              Download ({megabytes(w.entry.downloadBytes)})
            </button>
          )}
          <button type="button" className="app-banner-close" aria-label="Dismiss" onClick={() => setDismissedWaits((d) => new Set([...d, `${w.entry.id}:${w.evicted ? 'evicted' : 'waiting'}`]))}>
            <Icon name="x" />
          </button>
        </div>
      ))}
      {down.map((m) => {
        const name = models.find((x) => x.id === m.modelId)?.name ?? m.modelId;
        return (
          <div className="app-banner warn" role="status" data-banner="model-unavailable" data-model={m.modelId} key={`down-${m.modelId}`}>
            <Icon name="circle-alert" />
            <span>
              {name} is not answering: {formatExact(m.pending)} message{m.pending === 1 ? ' waits' : 's wait'} for it. Digester tries again;
              after a few tries the next scoring rules decide without it.
            </span>
            <button type="button" className="text-link" onClick={() => navigate(`/settings/models/${m.modelId}`)}>
              {name}
            </button>
            <button type="button" className="app-banner-close" aria-label="Dismiss" onClick={() => setDismissedDown((d) => new Set([...d, m.modelId]))}>
              <Icon name="x" />
            </button>
          </div>
        );
      })}
      {durable === false ? (
        <div className="app-banner warn" role="status" data-banner="storage">
          <Icon name="hard-drive" />
          <span>This browser cannot keep data after you close the tab.</span>
        </div>
      ) : null}
      {limitToday && current ? (
        <div className="app-banner" role="status" data-banner="daily-limit">
          <Icon name="circle-alert" />
          <span>Today's limit of {formatExact(current.max)} new messages is reached. Further items are skipped until tomorrow.</span>
          <button type="button" className="text-link" onClick={() => navigate('/subscribe')}>
            More
          </button>
          <button type="button" className="app-banner-close" aria-label="Dismiss" onClick={() => setDismissedDay(utcToday())}>
            <Icon name="x" />
          </button>
        </div>
      ) : null}
      {pauseShown && paused ? (
        <div className="app-banner" role="status" data-banner="scoring-paused" data-reason={paused.reason}>
          <Icon name="circle-alert" />
          <span>{scoringPauseText(paused)}</span>
          {paused.reason === 'budget' ? (
            <button type="button" className="text-link" onClick={() => navigate('/settings/models/jev')}>
              Change budget
            </button>
          ) : (
            <button type="button" className="text-link" onClick={() => navigate('/subscribe')}>
              More
            </button>
          )}
          <button type="button" className="app-banner-close" aria-label="Dismiss" onClick={() => setDismissedPause(paused.until)}>
            <Icon name="x" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
